import { computed, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';
import {
  CANVAS_OUTER_PADDING,
  CANVAS_ZOOM_TRANSITION_EASING,
  CANVAS_ZOOM_TRANSITION_MS,
} from '../lib/canvas-constants';
import { sheetWidthFor } from '../lib/sheet-pages';
import { useCanvasResponsiveWidth } from './useCanvasResponsiveWidth';

const SCROLL_CONTAINER_SELECTOR = '[data-testid="zoom-wrapper"]';

const HEADER_TRANSITION = `margin-left ${CANVAS_ZOOM_TRANSITION_MS}ms ${CANVAS_ZOOM_TRANSITION_EASING}`;

export function useCanvasHeaderOffset(
  containerRef: Ref<HTMLElement | null>,
  orientation: Ref<'portrait' | 'landscape'>,
  zoom: Ref<number>
) {
  const { containerWidth } = useCanvasResponsiveWidth(containerRef);

  // The paper is centred inside ZoomableCanvas' scrolling element, whose content box is narrower
  // than the stage container by the scrollbar. Centring against the container alone drifts the
  // header half a scrollbar to the right of the paper.
  const scrollContainerRef = ref<HTMLElement | null>(null);
  const resolveScrollContainer = () => {
    scrollContainerRef.value =
      containerRef.value?.querySelector<HTMLElement>(SCROLL_CONTAINER_SELECTOR) ?? null;
  };

  onMounted(resolveScrollContainer);
  watch(containerRef, resolveScrollContainer);

  const { containerWidth: scrollContainerWidth } = useCanvasResponsiveWidth(scrollContainerRef);

  const centringWidth = computed(() => scrollContainerWidth.value || containerWidth.value);

  const canvasDisplayWidth = computed(() => sheetWidthFor(orientation.value));

  const headerOffset = computed(() => {
    const scaledCanvasWidth = canvasDisplayWidth.value * zoom.value;
    // A sheet wider than the canvas starts at the ZoomableCanvas side padding, not centred.
    return Math.max((centringWidth.value - scaledCanvasWidth) / 2, CANVAS_OUTER_PADDING);
  });

  const isZoomAnimating = ref(false);
  let zoomAnimationTimer: ReturnType<typeof setTimeout> | null = null;

  const clearZoomAnimationTimer = () => {
    if (zoomAnimationTimer !== null) {
      clearTimeout(zoomAnimationTimer);
      zoomAnimationTimer = null;
    }
  };

  watch(zoom, (next, previous) => {
    if (next === previous) return;

    isZoomAnimating.value = true;
    clearZoomAnimationTimer();

    zoomAnimationTimer = setTimeout(() => {
      isZoomAnimating.value = false;
      zoomAnimationTimer = null;
    }, CANVAS_ZOOM_TRANSITION_MS);
  });

  onBeforeUnmount(clearZoomAnimationTimer);

  const headerStyle = computed(() => ({
    marginLeft: `${headerOffset.value}px`,
    transition: isZoomAnimating.value ? HEADER_TRANSITION : 'none',
  }));

  return { headerOffset, headerStyle, canvasDisplayWidth, containerWidth };
}
